import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { aiSettingsService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { isAuthenticatedAtom } from './use-auth';
import type { AiSettings } from '@/types';

// The brand is in every settings key as well as in the request. Two brands must never
// read each other's cached settings, and switching brands no longer clears the cache —
// a key without the brand would serve the previous brand's values.
const aiSettingsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['brand', 'ai-settings', buScopeKey(get)],
  queryFn: () => aiSettingsService.getAiSettings(buScopeParams(get)),
  staleTime: 5 * 60 * 1000,
  enabled: get(isAuthenticatedAtom)
}));

// React Query v5 dropped per-query onError, so the fetch is logged from inside the
// queryFn (function name + what failed) and rethrown — the query still settles into an
// error state, which is what callers key off to avoid blocking a save on this lookup.
//
// Keyed by brand, like the settings query above: a store belongs to one brand, so two
// brands on an account have two timezones and a shared cache entry would serve one of them
// the other's.
const storeTimezoneQueryAtom = atomWithQuery((get) => ({
  queryKey: ['brand', 'ai-settings', 'store-timezone', buScopeKey(get)],
  queryFn: async () => {
    try {
      return await aiSettingsService.getStoreTimezone(buScopeParams(get));
    } catch (error) {
      console.error('[useStoreTimezone] failed to fetch the Shopify store timezone', { error });
      throw error;
    }
  },
  staleTime: 5 * 60 * 1000,
  enabled: get(isAuthenticatedAtom)
}));

// `(get)` rather than `()`: the update carries the selected brand, read off the atom.
const updateAiSettingsMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['brand', 'ai-settings', 'update'],
  mutationFn: (payload: AiSettings) => aiSettingsService.updateAiSettings(payload, buScopeParams(get)),
  // Returned, so the save stays pending until the new settings load: each save sends the whole object.
  onSuccess: () => invalidateQueries(['brand', 'ai-settings'])
}));

export function useAiSettings() {
  const [query] = useAtom(aiSettingsQueryAtom);
  return query;
}

export function useUpdateAiSettings() {
  const [mutation] = useAtom(updateAiSettingsMutationAtom);
  return mutation;
}

export function useStoreTimezone() {
  const [query] = useAtom(storeTimezoneQueryAtom);
  return query;
}
